<script setup lang="ts">
import BrandsSearchResultTable from '~/widgets/search/brand/BrandsSearchResultTable.vue'
import SearchResultEmpty from '~/widgets/search/products/SearchResultEmpty.vue'
import SeoProductDescription from '~/entities/products/SeoProductDescription.vue'
import config from '~~/shared/config'
import useMainStore from '~~/shared/store'
import MainTitle from '~/components/ui/title/MainTitle.vue'
import Pagination from '~/components/ui/pagination/Pagination.vue'
import { useMonitorSize } from '~/composables/monitor-size'
import { useRoute, useRequestURL } from '#app'
import type { IBrandsTableRow } from '~/shared/interfaces/search/brands'
import type { IMonitorSize } from '~/shared/interfaces/composables'

definePageMeta({
  layout: 'search',
})

let sizes: IMonitorSize | null = useMonitorSize()
const paginationType = computed(() =>
  sizes?.isMobile ? 'watch-more' : 'pagination'
)
const mainStore = useMainStore()
const route = useRoute()
const { defaultCitySlug } = config()
const article = route.params.id as string
const brandsTime = ref<string | null>(null)
const brandsInfo = ref<IBrandsTableRow[]>([])
const pageCount = computed(() =>
  Math.ceil(brandsInfo.value.length / params.hitsPerPage)
)
const params = reactive({
  hitsPerPage: sizes.isMobile ? 12 : 24,
  page: 1,
})

const searchResults = computed(() => {
  const startIndex = sizes.isMobile ? 0 : (params.page - 1) * params.hitsPerPage
  const endIndex = params.page * params.hitsPerPage
  return brandsInfo.value.slice(startIndex, endIndex)
})

async function getBrandsList(page: number = 1) {
  mainStore.setContentLoadingStatus(false)

  const citySlug = mainStore.sotransCitySelected?.slug ?? defaultCitySlug
  const { data: brands } = await useFetch(`/api/search/brands/${article}`, {
    query: {
      citySlug,
    },
  })
  if (brands.value) {
    brandsInfo.value = brands.value.data
    brandsTime.value = (brands.value.time / 1000).toFixed(2)
    console.log(`Скорость поиска: ${brandsTime.value} сек`)
    params.page = page
  }
  mainStore.setContentLoadingStatus(true)
}

function setPage(page: number) {
  params.page = page
  getBrandsList(params.page)
}

function handleWatchMore(page: number) {
  params.page = page
}

watch(
  () => mainStore.sotransCitySelected,
  async () => {
    params.page = 1
    await getBrandsList()
  }
)

await getBrandsList()
</script>

<template>
  <MainTitle
    v-if="brandsInfo.length"
    class="search-brands__title"
    :text="`По запросу «${route.params.id}» найдено:`"
    :text-with-seo="true"
    size="xs"
  >
    <div class="search-brands__request">
      <p><span>Количество результатов:</span> {{ brandsInfo?.length }}</p>
    </div>
  </MainTitle>
  <div class="brands">
    <BrandsSearchResultTable
      v-if="searchResults && searchResults.length"
      :search-results="searchResults"
      :offers-cell="true"
    />
    <SearchResultEmpty v-else page="brands" />
  </div>
  <div class="pagination">
    <Pagination
      v-if="pageCount > 1"
      :pagination-type="paginationType"
      :last-page="pageCount"
      :current-page="params.page"
      @handle-pagination="setPage"
      @handle-watch-more="handleWatchMore"
    />
  </div>
  <SeoProductDescription
    v-if="mainStore.seoAdditionalText"
    :additional-text="mainStore.seoAdditionalText"
  />
</template>

<style lang="scss" scoped>
.search-request__name {
  text-transform: uppercase;
}

.pagination {
  @include md-breakpoint {
    padding: 20px 10px 0;
  }
}
</style>
